import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import api from '../../../api';
import { AuthContext } from '../../../contexts/AuthContext';
import { useGroupCore } from './GroupCoreContext';
import { useFacility } from './FacilityContext';

/**
 * Context for managing listings within facilities
 */
export const ListingContext = createContext();

/**
 * Create a new listing with all required fields based on backend requirements
 * @param {string} facilityName - Name of the facility for the title
 * @returns {Object} - New listing with all required fields
 */
const createEmptyListing = (facilityName = 'New Facility') => {
  return {
    // Required base fields
    sourceListingId: `temp-${Date.now()}-${Math.floor(Math.random()*1000)}`,
    title: `${facilityName} Position`,
    
    // Required compensation structure
    compensation: {
      amount: 80,
      period: 'hourly',
      contractType: 'W2',
      includesHousing: false,
      includesTravel: false,
      malpracticeCovered: false,
      signOnBonusOffered: false,
      signOnBonusAmount: 0,
      signOnBonusCommitmentYears: 0
    },
    
    // Assignment details
    assignment: {
      startDate: '',
      types: []
    },
    
    // Schedule details
    schedule: {
      pattern: 'flexible',
      estimatedWeeklyHours: 40,
      onCall: false,
      weekends: false
    },
    
    // Clinical requirements
    clinical: {
      requiredCaseTypes: [],
      specialties: []
    },
    
    // Other fields
    tags: []
  };
};

/**
 * Provider for listing operations and state
 */
export const ListingProvider = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { /* existingGroupId - removed unused variable */ setErrors } = useGroupCore();
  const { facilities, previewFacilityIndex: facilityContextPreviewIndex } = useFacility(); // Get the active facility index
  
  // Store fetched listings by facilityId
  const [listingsByFacilityId, setListingsByFacilityId] = useState({});
  
  // Listing preview state
  const [previewListingIndex, setPreviewListingIndex] = useState(0);
  const [previewFacilityIndex, setPreviewFacilityIndex] = useState(0);
  
  // Delete confirmation state
  const [showDelListModal, setShowDelListModal] = useState(false);
  const [listToRemove, setListToRemove] = useState({ facIdx: null, listIdx: null });
  
  // User input tracking to prevent race conditions
  // This tracks recent user inputs with timestamps to avoid them being overwritten
  const userInputTracker = React.useRef({});
  
  // Pending updates tracker for debounced fields
  // Tracks updates that are waiting to be sent to the API after debounce
  const pendingUpdates = React.useRef({});
  
  // Debounce timers for various fields
  const debounceTimers = React.useRef({});
  
  // Configuration values
  const USER_INPUT_PROTECTION_WINDOW = 5000; // 5 seconds for race condition prevention
  const DEBOUNCE_DELAY = 800; // 800ms debounce for slider inputs
  
  // Debugging helper for API responses
  // Determine active facility
  const activeFacility = useMemo(() => {
    if (facilities && typeof facilityContextPreviewIndex === 'number' && facilityContextPreviewIndex >= 0 && facilityContextPreviewIndex < facilities.length) {
        return facilities[facilityContextPreviewIndex];
    }
    return null;
  }, [facilities, facilityContextPreviewIndex]);
  const activeFacilityId = useMemo(() => activeFacility?.id || null, [activeFacility]);

  // Query to fetch listings for the currently active facility
  const {
    // We don't need to capture the data directly as we handle it in onSuccess
    isLoading: isLoadingListingsForActiveFacility,
    // error: errorLoadingListings, // Can be handled if needed by exposing it
  } = useQuery({
    queryKey: ['listings', 'facility', activeFacilityId],
    queryFn: async () => {
      if (!activeFacilityId || !auth.token) {
        console.log('ListingContext: No active facility ID or auth token, skipping listings fetch.');
        return [];
      }
      console.log('Fetching listings for facility:', activeFacilityId);
      try {
        const response = await api.get(`/facilities/${activeFacilityId}/listings`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        
        // Make sure response.data is normalized to an array
        const responseData = response.data || [];
        const listingsArray = Array.isArray(responseData) ? responseData : [responseData];
        
        console.log(`Fetched ${listingsArray.length} listings for facility ${activeFacilityId}:`, listingsArray);
        return listingsArray;
      } catch (error) {
        console.error('Error fetching listings for facility:', error);
        return [];
      }
    },
    enabled: !!activeFacilityId && !!auth.token,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once on failure
    onSuccess: (data) => {
      console.log(`Successfully fetched listings for facility ${activeFacilityId}:`, data);
      
      if (activeFacilityId) {
        // Apply user input protection before updating state
        const userInputsForFacility = userInputTracker.current[activeFacilityId] || {};
        const now = Date.now();
        
        // Normalize the listings data
        const normalizedData = (data || []).map((listing, index) => {
          const listingId = listing.id || listing._id;
          const userInputsForListing = userInputsForFacility[index] || {};
          
          // Start with the fetched listing
          let processedListing = {
            ...listing,
            id: listingId // Ensure consistent id field
          };
          
          // Apply any recent user inputs that shouldn't be overwritten
          Object.entries(userInputsForListing).forEach(([fieldPath, { value, timestamp }]) => {
            // Only preserve user inputs that are within the protection window
            if (now - timestamp < USER_INPUT_PROTECTION_WINDOW) {
              console.log(`Preserving user input for ${fieldPath} in listing ${listingId || index}:`, value);
              
              // Handle nested paths like 'schedule.pattern' or 'compensation.amount'
              if (fieldPath.includes('.')) {
                const [parentField, childField] = fieldPath.split('.');
                processedListing[parentField] = {
                  ...(processedListing[parentField] || {}),
                  [childField]: value
                };
              } else {
                // Direct field
                processedListing[fieldPath] = value;
              }
            }
          });
          
          return processedListing;
        });
        
        // Update our local state with the protected listings
        setListingsByFacilityId(prev => ({
          ...prev,
          [activeFacilityId]: normalizedData
        }));
      }
    },
    onError: (error) => {
      console.error('Error fetching listings:', error);
      // Optionally clear listings for this facility on error or ensure it's an empty array
      if (activeFacilityId) {
        setListingsByFacilityId(prev => ({
          ...prev,
          [activeFacilityId]: []
        }));
      }
    }
  });

  // Memoized listings for the currently active/previewed facility from local state
  const currentFacilityListings = useMemo(() => {
    return listingsByFacilityId[activeFacilityId] || [];
  }, [listingsByFacilityId, activeFacilityId]);

  const logApiResponse = (label, response) => {
    console.log(`API ${label}:`, response);
    return response;
  };

  // API Mutations
  const { mutate: createListing } = useMutation({
    mutationFn: ({ facilityId, data }) => {
      if (!auth.token) throw new Error('Authentication required');
      return api.post(`/facilities/${facilityId}/listings`, data, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
    },
    onSuccess: (response, variables) => { // variables = { facilityId, data: originalSentData }
      console.log('Successfully created listing:', response.data);
      const newListingFromServer = response.data;

      // Optimistically update the react-query cache
      queryClient.setQueryData(['listings', 'facility', variables.facilityId], (oldListings) => {
        const newList = oldListings ? [...oldListings] : [];
        // Ensure new listing has an 'id' for consistency if relying on it for keys/updates
        const listingToAdd = { ...newListingFromServer, id: newListingFromServer.id || newListingFromServer._id };
        newList.push(listingToAdd);
        return newList;
      });

      // Update local state `listingsByFacilityId` as well
      setListingsByFacilityId(prev => {
        const currentListings = prev[variables.facilityId] || [];
        const listingToAdd = { ...newListingFromServer, id: newListingFromServer.id || newListingFromServer._id };
        return {
          ...prev,
          [variables.facilityId]: [...currentListings, listingToAdd],
        };
      });
      // No immediate invalidation to prevent rapid re-fetches; rely on optimistic update.
    },
    onError: (error) => {
      console.error('Failed to create listing:', error);
      setErrors(prev => ({
        ...prev,
        listings: 'Failed to create listing. Please try again.'
      }));
    }
  });
  
  const { mutate: updateListing } = useMutation({
    mutationFn: ({ facilityId, listingId, data }) => {
      if (!auth.token) throw new Error('Authentication required');
      
      // Special handling for contract type updates to ensure proper format
      if (data.compensation && data.compensation.contractType) {
        console.log(`Ensuring contractType is properly formatted: ${data.compensation.contractType}`);
        // Make sure contractType is a string
        data.compensation.contractType = String(data.compensation.contractType);
      }
      
      console.log(`API PUT call: /facilities/${facilityId}/listings/${listingId}`, data);
      
      return api.put(`/facilities/${facilityId}/listings/${listingId}`, data, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
    },
    onSuccess: (response, variables) => { // variables = { facilityId, listingId, data: originalSentDataFromMutationCall }
      console.log('Successfully updated listing:', response.data);
      const updatedListingFromServer = response.data;

      // Optimistically update the react-query cache
      queryClient.setQueryData(['listings', 'facility', variables.facilityId], (oldCachedListings) => {
        if (!oldCachedListings) return []; // Should not happen if query has run and fetched data
        return oldCachedListings.map(listing =>
          (listing.id === variables.listingId || listing._id === variables.listingId)
            // Merge existing listing with server response, ensuring 'id' is present
            ? { 
                ...listing, // Keep local state not overwritten by server
                ...updatedListingFromServer, // Apply server updates
                id: updatedListingFromServer.id || updatedListingFromServer._id || variables.listingId // Ensure ID
              }
            : listing
        );
      });

      // Update local state `listingsByFacilityId` as well.
      // This is for any components that might still be directly using this local state
      // instead of solely relying on react-query's cache via `currentFacilityListings`.
      if (variables.facilityId) {
        setListingsByFacilityId(prevListingsByFacility => {
          const currentListingsForFacility = prevListingsByFacility[variables.facilityId] || [];
          const updatedListingsForFacility = currentListingsForFacility.map(listing =>
            (listing.id === variables.listingId || listing._id === variables.listingId)
              ? { 
                  ...listing, 
                  ...updatedListingFromServer, 
                  id: updatedListingFromServer.id || updatedListingFromServer._id || variables.listingId 
                }
              : listing
          );
          return {
            ...prevListingsByFacility,
            [variables.facilityId]: updatedListingsForFacility,
          };
        });
      }
      // No immediate invalidation or manual GET to prevent rapid re-fetches.
      // Rely on optimistic update. If background re-validation is desired later,
      // queryClient.invalidateQueries(['listings', 'facility', variables.facilityId]) can be added,
      // but it will cause a refetch. Consider staleTime/cacheTime for data freshness.
    },
    onError: (error) => {
      console.error('Failed to update listing:', error);
      setErrors(prev => ({
        ...prev,
        listings: 'Failed to update listing. Please try again.'
      }));
    }
  });
  
  const { mutate: deleteListing } = useMutation({
    mutationFn: ({ facilityId, listingId }) => {
      console.log(`Deleting listing: facilityId=${facilityId}, listingId=${listingId}`);
      if (!auth.token) throw new Error('Authentication required');
      return api.delete(`/facilities/${facilityId}/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
      .then(response => logApiResponse('delete success', response))
      .catch(error => {
        console.error('API delete error:', error);
        throw error;
      });
    },
    onSuccess: (response, { facilityId, listingId }) => {
      console.log('Successfully deleted listing:', listingId);
      
      // Update cache
      queryClient.invalidateQueries(['listings', 'facility', facilityId]);
      
      // Update UI by removing the deleted listing
      setListingsByFacilityId(prev => {
        const currentListings = [...(prev[facilityId] || [])];
        const updatedListings = currentListings.filter(
          listing => listing.id !== listingId && listing._id !== listingId
        );
        
        console.log(`Removed listing ${listingId} from facility ${facilityId}`);
        console.log(`Before: ${currentListings.length}, After: ${updatedListings.length}`);
        
        return {
          ...prev,
          [facilityId]: updatedListings
        };
      });
      
      // Close the delete modal
      setShowDelListModal(false);
      // Reset the listToRemove state
      setListToRemove({ facIdx: null, listIdx: null });
    },
    onError: (error) => {
      console.error('Failed to delete listing:', error);
      setErrors(prev => ({
        ...prev,
        listings: 'Failed to delete listing. Please try again.'
      }));
      
      // Close the delete modal even on error
      setShowDelListModal(false);
      // Reset the listToRemove state even on error
      setListToRemove({ facIdx: null, listIdx: null });
    }
  });
  
  // Add a new listing to a facility
  const addListing = useCallback((facilityIndex) => {
    // Get the facility at this index
    const facility = facilities[facilityIndex];
    if (!facility) {
      console.error('Cannot add listing: facility not found at index', facilityIndex);
      return;
    }
    
    // Get facility ID
    const facilityId = facility.id || facility._id;
    if (!facilityId) {
      console.error('Cannot add listing: facility ID is missing');
      return;
    }
    
    // Create a new listing with required fields
    const newListing = createEmptyListing(facility.facilityName || facility.name);
    
    // Create the listing in the database
    createListing({ facilityId, data: newListing });
  }, [facilities, createListing]);
  
  // Update a specific field in a listing
  const updateListingField = useCallback((facilityIndex, listingIndex, field, value) => {
    console.log(`Updating listing field: ${field} with value: ${value}`);
    
    // Validate inputs
    if (!facilities || facilityIndex >= facilities.length) {
      console.error(`Invalid facility index: ${facilityIndex} (total facilities: ${facilities?.length})`);
      return;
    }
    
    // Get the facility
    const facility = facilities[facilityIndex];
    if (!facility) {
      console.error('Facility not found at index:', facilityIndex);
      return;
    }
    
    // Get facility ID
    const facilityId = facility.id || facility._id;
    if (!facilityId) {
      console.error('Facility ID is missing');
      return;
    }
    
    // Get the listing
    const listings = listingsByFacilityId[facilityId] || [];
    const listing = listings[listingIndex];
    const listingId = listing?.id || listing?._id;
    
    if (!listingId) {
      console.error('Listing ID is missing');
      return;
    }
    
    console.log(`Updating listing ${listingId} field: ${field} with value:`, value);
    
    // ==== IMMEDIATE UI UPDATE FOR RESPONSIVENESS ====
    // This is critical for making the UI responsive to all user inputs
    // Always update UI state immediately for all field types
    const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
    const updatedCachedListings = cachedListings.map(cachedListing => {
      if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
        return cachedListing;
      }
      
      // Handle different field types appropriately
      if (field.includes('.')) {
        // Handle nested fields (e.g., compensation.amount, assignment.type)
        const [parent, child] = field.split('.');
        return {
          ...cachedListing,
          [parent]: {
            ...(cachedListing[parent] || {}),
            [child]: value
          }
        };
      } else {
        // Handle direct fields (e.g., title)
        return {
          ...cachedListing,
          [field]: value
        };
      }
    });
    
    // Always update the local state immediately for better responsiveness
    // This prevents loss of user input and makes the UI feel responsive
    setListingsByFacilityId(prev => ({
      ...prev,
      [facilityId]: updatedCachedListings
    }));
    
    console.log(`Found facility: ${facility.name || facility.facilityName} (ID: ${facilityId})`);
    
    // Track this user input to prevent it from being overwritten by the props sync
    if (!userInputTracker.current[facilityId]) {
      userInputTracker.current[facilityId] = {};
    }
    if (!userInputTracker.current[facilityId][listingIndex]) {
      userInputTracker.current[facilityId][listingIndex] = {};
    }
    userInputTracker.current[facilityId][listingIndex][field] = {
      value: value,
      timestamp: Date.now()
    };
    
    // Get all listings for this facility - we've already obtained the listing earlier
    // so we don't need to get it again
    const facilityListings = facility.listings || [];
    const allCachedListings = listingsByFacilityId[facilityId] || [];
    
    // For debugging, log both sources of listings
    console.log(`Cached listings: ${allCachedListings.length}, Facility listings: ${facilityListings.length}`);
    
    // We've already verified the listing exists earlier in the function
    console.log(`Proceeding with update to listing ${listingId}`);
    
    // Handle the case where there are no listings yet - we should never reach here
    // since we've already verified the listing exists earlier
    if (allCachedListings.length === 0 && facilityListings.length === 0) {
      // If we're trying to update a non-existent listing, create a new one
      console.log('No listings found, creating a new one');
      addListing(facilityIndex);
      return;
    }
    
    // We've already validated the listing index and obtained the listing and listingId earlier
    // This is a safeguard check in case we somehow reach this point with invalid data
    console.log(`Continuing with update for listing: ${listing?.title || 'Untitled'} with ID ${listingId} at index ${listingIndex}`);
    
    if (!listingId) {
      console.error('Listing ID is missing in update process');
      return;
    }
    
    // Create update data based on the field
    let updateData = {};
    
    // Special handling for clinicalRequirements.requiredCaseTypes to handle array values
    if (field === 'clinicalRequirements.requiredCaseTypes' || field === 'case.types') {
      console.log(`Special handling for case type: ${value}`);
      
      // Get the current case types from both possible locations
      // The backend uses 'clinical.requiredCaseTypes' but our UI uses 'case.types' or 'clinicalRequirements.requiredCaseTypes'
      const currentClinical = listing.clinical || {};
      const currentRequirements = listing.clinicalRequirements || {};
      
      // Get existing case types or initialize empty array (check both locations)
      let currentCaseTypes = Array.isArray(currentClinical.requiredCaseTypes) ? 
                            [...currentClinical.requiredCaseTypes] : 
                            Array.isArray(currentRequirements.requiredCaseTypes) ? 
                            [...currentRequirements.requiredCaseTypes] : 
                            [];
      
      console.log('Current case types before toggle:', currentCaseTypes);
      
      // Toggle the value - add if not present, remove if already present
      if (currentCaseTypes.includes(value)) {
        currentCaseTypes = currentCaseTypes.filter(t => t !== value);
      } else {
        currentCaseTypes.push(value);
      }
      
      console.log('Current case types after toggle:', currentCaseTypes);
      
      // The backend expects this field in clinical.requiredCaseTypes
      // Only send that to avoid duplicate API calls, but track both for UI
      updateData = {
        clinical: {
          ...currentClinical,
          requiredCaseTypes: currentCaseTypes
        }
        // We don't need to send clinicalRequirements to the API
        // This is just for UI display, but we'll track it for internal state
      };
      
      // CRITICAL: Immediately update the UI state for responsiveness
      // We need to update both clinical.requiredCaseTypes and clinicalRequirements.requiredCaseTypes
      // to ensure consistent UI rendering, as components might check either location
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Create comprehensive update with all possible fields used in the UI
        return {
          ...cachedListing,
          // Update clinicalRequirements for UI components that check this path
          clinicalRequirements: {
            ...(cachedListing.clinicalRequirements || {}),
            requiredCaseTypes: currentCaseTypes
          },
          // Update clinical for backend data structure
          clinical: {
            ...(cachedListing.clinical || {}),
            requiredCaseTypes: currentCaseTypes
          },
          // Also set flat field for components that might use it directly
          _requiredCaseTypes: currentCaseTypes
        };
      });
      
      // Update the local cache without triggering an API call for these fields
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      console.log('Case types update data sent to API:', updateData);
      console.log('Final case types:', currentCaseTypes);
    }
    // Special handling for schedule.hours to ensure it's properly preserved in the UI
    else if (field === 'schedule.hours') {
      console.log(`Special handling for schedule hours: ${value}`);
      const currentSchedule = listing.schedule || {};
      const numericValue = Number(value);
      
      // Only send what the backend needs - estimatedWeeklyHours within the schedule object
      updateData = {
        schedule: {
          ...currentSchedule,
          estimatedWeeklyHours: numericValue  // Correct field name for backend DTO
        }
        // Don't send the flat estimatedWeeklyHours field to the API
      };
      
      // Update the local state to keep UI in sync
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
          return {
            ...cachedListing,
            estimatedWeeklyHours: numericValue // Flat field for UI
          };
        }
        return cachedListing;
      });
      
      // Update the local cache without triggering additional API calls
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      console.log('Schedule hours update data sent to API:', updateData);
    }
    // Special handling for includesHousing checkbox to ensure it's properly stored in compensation
    else if (field === 'includesHousing') {
      console.log(`Special handling for includesHousing: ${value}`);
      const currentComp = listing.compensation || {};
      
      // Only send what the backend needs - store in compensation as the backend expects
      updateData = {
        compensation: {
          ...currentComp,
          includesHousing: value  // Store in compensation as the backend expects
        }
      };
      
      // CRITICAL: Update the local state immediately for UI responsiveness
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Return updated listing with both flat and nested field updated
        return {
          ...cachedListing,
          includesHousing: value, // Flat field for UI
          compensation: {
            ...(cachedListing.compensation || {}),
            includesHousing: value
          }
        };
      });
      
      // Update the local cache immediately for UI responsiveness
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      // Note: The API update will be debounced by the code below
    }
    // Special handling for includesTravel checkbox to ensure it's properly stored in compensation
    else if (field === 'includesTravel') {
      console.log(`Special handling for includesTravel: ${value}`);
      const currentComp = listing.compensation || {};
      
      // Only send what the backend needs - in the compensation object
      updateData = { 
        compensation: {
          ...currentComp,
          includesTravel: value  // Store in compensation as the backend expects
        }
      };
      
      // CRITICAL: Update the local state immediately for UI responsiveness
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Return updated listing with both flat and nested field updated
        return {
          ...cachedListing,
          includesTravel: value, // Flat field for UI
          compensation: {
            ...(cachedListing.compensation || {}),
            includesTravel: value
          }
        };
      });
      
      // Update the local cache immediately for UI responsiveness
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      // Note: The API update will be debounced by the code below
    }
    // Special handling for schedule.onCall to ensure it's properly persisted
    else if (field === 'schedule.onCall') {
      console.log(`Special handling for schedule onCall: ${value}`);
      const currentSchedule = listing.schedule || {};
      
      // Only send what the backend needs - in the schedule object
      updateData = {
        schedule: {
          ...currentSchedule,
          onCall: value  // Store in the schedule object for the backend DTO
        }
      };
      
      // CRITICAL: Update the local state immediately for UI responsiveness
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Return updated listing with both flat and nested fields updated
        return {
          ...cachedListing,
          schedule: {
            ...(cachedListing.schedule || {}),
            onCall: value
          }
        };
      });
      
      // Update the local cache immediately for UI responsiveness
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      // Note: The API update will be debounced by the code below
    }
    // Special handling for schedule.weekends to ensure it's properly persisted
    else if (field === 'schedule.weekends') {
      console.log(`Special handling for schedule weekends: ${value}`);
      const currentSchedule = listing.schedule || {};
      
      // Only send what the backend needs - in the schedule object
      updateData = {
        schedule: {
          ...currentSchedule,
          weekends: value  // Store in the schedule object for the backend DTO
        }
      };
      
      // CRITICAL: Update the local state immediately for UI responsiveness
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Return updated listing with both flat and nested fields updated
        return {
          ...cachedListing,
          schedule: {
            ...(cachedListing.schedule || {}),
            weekends: value
          }
        };
      });
      
      // Update the local cache immediately for UI responsiveness
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      // Note: The API update will be debounced by the code below
    }
    // Special handling for schedule.type to ensure it's properly displayed in the UI
    // Note: Backend expects 'pattern' not 'type' for schedule
    else if (field === 'schedule.type') {
      console.log(`Special handling for schedule pattern: ${value}`);
      const currentSchedule = listing.schedule || {};
      
      // Only send what the backend needs - use 'pattern' which is the correct field name
      updateData = {
        schedule: {
          ...currentSchedule,
          pattern: value  // Use 'pattern' which is the correct field name for the backend DTO
        }
      };
      
      // CRITICAL: Update the local state immediately for UI responsiveness
      // We update both schedule.pattern and scheduleType to ensure consistent UI display
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Create updated listing with both fields set for consistent UI
        return {
          ...cachedListing,
          scheduleType: value, // Flat field for UI display
          schedule: {
            ...(cachedListing.schedule || {}),
            pattern: value  // Nested field for backend data model
          }
        };
      });
      
      // Update the local cache immediately for UI responsiveness
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      // Note: The API update will be debounced by the code below
    }
    // Special handling for assignment.type to ensure it's stored as types array for UI consistency
    else if (field === 'assignment.type') {
      console.log(`Special handling for assignment type: ${value}`);
      const currentAssignment = listing.assignment || {};
      
      // Get current types or initialize an empty array
      const currentTypes = Array.isArray(currentAssignment.types) ? [...currentAssignment.types] : [];
      
      // Toggle the selected value (add if not present, remove if already present)
      let updatedTypes;
      if (currentTypes.includes(value)) {
        updatedTypes = currentTypes.filter(type => type !== value);
      } else {
        updatedTypes = [...currentTypes, value];
      }
      
      console.log(`Toggling assignment type ${value}. Before: [${currentTypes.join(', ')}], After: [${updatedTypes.join(', ')}]`);
      
      // Only send what the backend needs - in the assignment object
      updateData = {
        assignment: {
          ...currentAssignment,
          types: updatedTypes // Backend expects an array of types
        }
      };
      
      // CRITICAL: Update the local state immediately for UI responsiveness 
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id !== listingId && cachedListing._id !== listingId)) {
          return cachedListing;
        }
        
        // Update both the types array and the legacy type field for maximum UI compatibility
        return {
          ...cachedListing,
          assignment: {
            ...(cachedListing.assignment || {}),
            types: updatedTypes,
            type: updatedTypes // Also set as direct type for backward compatibility
          }
        };
      });
      
      // Update the local cache without triggering additional API calls
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      console.log('Assignment type update data sent to API:', updateData);
    }
    // Special handling for compensation fields which are critical for the backend
    else if (field.startsWith('compensation.') || field === 'compensation') {
      // Ensure we have a complete compensation object
      const currentComp = listing.compensation || {
        amount: 80,
        period: 'hourly',
        contractType: 'W2',
        signOnBonusOffered: false
      };
      
      if (field === 'compensation') {
        // If updating the entire compensation object
        updateData = { compensation: { ...currentComp, ...value } };
        
        // Update the local state to keep UI in sync
        const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
        const updatedCachedListings = cachedListings.map(cachedListing => {
          if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
            return {
              ...cachedListing,
              compensation: { ...currentComp, ...value } // Update the full compensation object
            };
          }
          return cachedListing;
        });
        
        // Update the local cache without triggering additional API calls
        setListingsByFacilityId(prev => ({
          ...prev,
          [facilityId]: updatedCachedListings
        }));
        
        // We'll handle debouncing at the end of the function for all slider fields
      } else if (field === 'compensation.contractType') {
        // Special handling for contract type - backend requires this specifically
        console.log(`Updating contract type to: ${value}`);
        updateData = { 
          compensation: { 
            ...currentComp,
            contractType: value 
          } 
        };
        
        // Update the local state to keep UI in sync
        const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
        const updatedCachedListings = cachedListings.map(cachedListing => {
          if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
            return {
              ...cachedListing,
              compensation: { 
                ...(cachedListing.compensation || {}),
                contractType: value 
              }
            };
          }
          return cachedListing;
        });
        
        // Update the local cache without triggering additional API calls
        setListingsByFacilityId(prev => ({
          ...prev,
          [facilityId]: updatedCachedListings
        }));
      } else {
        // If updating other fields within compensation
        const compField = field.split('.')[1];
        updateData = { 
          compensation: { 
            ...currentComp,
            [compField]: value 
          } 
        };
        
        // Update the local state to keep UI in sync
        const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
        const updatedCachedListings = cachedListings.map(cachedListing => {
          if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
            return {
              ...cachedListing,
              compensation: { 
                ...(cachedListing.compensation || {}),
                [compField]: value 
              }
            };
          }
          return cachedListing;
        });
        
        // Update the local cache without triggering additional API calls
        setListingsByFacilityId(prev => ({
          ...prev,
          [facilityId]: updatedCachedListings
        }));
        
        // We'll handle debouncing at the end of the function for all slider fields
      }
      
      console.log('Compensation update data:', updateData);
    }
    // Special handling for sign-on bonus fields to ensure they're properly stored in compensation
    else if (field === 'signOnBonusOffered' || field === 'signOnBonusAmount' || field === 'signOnBonusCommitmentYears') {
      console.log(`Special handling for sign-on bonus field ${field}: ${value}`);
      const currentComp = listing.compensation || {};
      
      // Only send what the backend needs - in the compensation object
      updateData = { 
        compensation: {
          ...currentComp,
          [field]: value  // Store in the compensation object as the backend expects
        }
      };
      
      // Update the local state to keep UI in sync
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
          return {
            ...cachedListing,
            [field]: value // Flat field for UI
          };
        }
        return cachedListing;
      });
      
      // Update the local cache without triggering additional API calls
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
      
      console.log(`Sign-on bonus ${field} update data sent to API:`, updateData);
    }
    // Handle other nested fields
    else if (field.includes('.')) {
      const parts = field.split('.');
      updateData = { [parts[0]]: { ...(listing[parts[0]] || {}) } };
      let current = updateData[parts[0]];
      
      // Build the nested structure
      for (let i = 1; i < parts.length - 1; i++) {
        current[parts[i]] = { ...(current[parts[i]] || {}) };
        current = current[parts[i]];
      }
      
      // Set the value at the final level
      current[parts[parts.length - 1]] = value;
      
      // Update the local state for UI consistency without redundant fields
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
          // Create a copy of the current listing to update
          const updatedListing = { ...cachedListing };
          let currentField = updatedListing;
          
          // Navigate to the right nesting level
          for (let i = 0; i < parts.length - 1; i++) {
            if (!currentField[parts[i]]) currentField[parts[i]] = {};
            currentField[parts[i]] = { ...currentField[parts[i]] };
            currentField = currentField[parts[i]];
          }
          
          // Update the value at the final level
          currentField[parts[parts.length - 1]] = value;
          return updatedListing;
        }
        return cachedListing;
      });
      
      // Update the local cache without triggering additional API calls
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
    } else {
      // Handle direct fields
      updateData[field] = value;
      
      // Also update the local state for UI consistency
      const cachedListings = [...(listingsByFacilityId[facilityId] || [])];
      const updatedCachedListings = cachedListings.map(cachedListing => {
        if ((cachedListing.id === listingId || cachedListing._id === listingId)) {
          return {
            ...cachedListing,
            [field]: value
          };
        }
        return cachedListing;
      });
      
      // Update the local cache without triggering additional API calls
      setListingsByFacilityId(prev => ({
        ...prev,
        [facilityId]: updatedCachedListings
      }));
    }
    
    console.log(`Update data for ${field}:`, updateData);
    
    // ALL fields should use debouncing for API calls to prevent network spam
    // Only explicitly exclude fields that MUST update immediately on the backend
    const fieldsToNotDebounce = [
      // Add any fields here that should NOT be debounced
      // e.g., fields that require immediate backend validation
    ];
    
    // Use debouncing for all fields unless explicitly excluded
    const shouldDebounce = !fieldsToNotDebounce.includes(field);
    
    if (shouldDebounce) {
      // Create a unique key for this listing's debounce
      const debounceKey = `${facilityId}-${listingId}-${field}`;
      
      // Store the latest update data for the API call
      pendingUpdates.current[debounceKey] = updateData;
      
      // Clear any existing timer for this field
      if (debounceTimers.current[debounceKey]) {
        clearTimeout(debounceTimers.current[debounceKey]);
      }
      
      console.log(`Debouncing API call for ${field} to prevent excessive network requests`);
      
      // Set a new timer
      debounceTimers.current[debounceKey] = setTimeout(() => {
        // Get the latest update that was stored
        const latestUpdate = pendingUpdates.current[debounceKey];
        if (latestUpdate) {
          // Send the update to the API
          console.log(`Sending debounced API update for listing ${listingId} field ${field}:`, latestUpdate);
          updateListing({ facilityId, listingId, data: latestUpdate });
          
          // Clear the pending update
          delete pendingUpdates.current[debounceKey];
        }
        
        // Clear the timer reference
        delete debounceTimers.current[debounceKey];
      }, DEBOUNCE_DELAY);
    } else {
      // For non-slider fields, send the update immediately
      console.log(`Sending immediate API update for listing ${listingId} field ${field}:`, updateData);
      updateListing({ facilityId, listingId, data: updateData });
    }
  }, [facilities, addListing, listingsByFacilityId, updateListing]);
  
  // Get listings for a facility from our cached state
  const getListingsForFacility = useCallback((facilityId) => {
    return listingsByFacilityId[facilityId] || [];
  }, [listingsByFacilityId]);
  
  // Update a clinical field in a listing
  const updateClinicalField = useCallback((facilityIndex, listingIndex, field, value) => {
    // Use the standard updateListingField with the 'clinical.' prefix
    updateListingField(facilityIndex, listingIndex, `clinical.${field}`, value);
  }, [updateListingField]);
  
  // Remove a listing - called when the modal's Yes button is clicked
  const removeListing = useCallback(() => {
    console.log('removeListing called with listToRemove:', listToRemove);
    
    // Use the facIdx and listIdx from listToRemove
    const facilityIndex = listToRemove.facIdx;
    const listingIndex = listToRemove.listIdx;
    
    if (facilityIndex === null || listingIndex === null) {
      console.error('Cannot remove listing: no listing selected');
      return;
    }
    
    console.log(`Attempting to remove listing at facility index ${facilityIndex}, listing index ${listingIndex}`);
    
    if (!facilities || facilityIndex >= facilities.length) {
      console.error('Invalid facility index');
      return;
    }
    
    const facility = facilities[facilityIndex];
    if (!facility) {
      console.error('Facility not found');
      return;
    }
    
    const facilityId = facility.id || facility._id;
    if (!facilityId) {
      console.error('Facility ID is missing');
      return;
    }
    
    console.log(`Got facility ID: ${facilityId}`);
    
    const listings = getListingsForFacility(facilityId);
    console.log(`Found ${listings.length} listings for facility ${facilityId}:`, listings);
    
    if (!listings || listingIndex >= listings.length) {
      console.error(`Invalid listing index: ${listingIndex} for ${listings.length} listings`);
      return;
    }
    
    const listing = listings[listingIndex];
    if (!listing) {
      console.error('Listing not found');
      return;
    }
    
    const listingId = listing.id || listing._id;
    if (!listingId) {
      console.error('Listing ID is missing');
      return;
    }
    
    console.log(`Deleting listing with ID: ${listingId}`);
    
    // Delete the listing from the server
    deleteListing({ facilityId, listingId });
  }, [facilities, deleteListing, listToRemove, getListingsForFacility]);
  
  // Confirm removal of a listing (show the modal)
  const confirmRemoveListing = useCallback((facilityIndex, listingIndex) => {
    console.log('confirmRemoveListing called with:', facilityIndex, listingIndex);
    // Store the indices of the listing to remove
    setListToRemove({ facIdx: facilityIndex, listIdx: listingIndex });
    // Show the confirmation modal
    setShowDelListModal(true);
    console.log('Delete modal should now be visible');
  }, [setListToRemove, setShowDelListModal]);
  
  // Cancel removal of a listing (hide the modal)
  const cancelRemoveListing = useCallback(() => {
    setShowDelListModal(false);
    setListToRemove({ facIdx: null, listIdx: null });
  }, []);
  
  // Load all listings for all facilities
  useEffect(() => {
    const loadListingsForFacilities = async () => {
      if (!auth.token || !facilities || facilities.length === 0) return;
      
      for (const facility of facilities) {
        const facilityId = facility.id || facility._id;
        if (!facilityId) continue;
        
        try {
          const response = await api.get(`/facilities/${facilityId}/listings`, {
            headers: { Authorization: `Bearer ${auth.token}` }
          });
          
          const listings = response.data || [];
          
          // Update our cache with the fetched listings
          setListingsByFacilityId(prev => ({
            ...prev,
            [facilityId]: listings
          }));
        } catch (error) {
          console.error(`Failed to load listings for facility ${facilityId}:`, error);
        }
      }
    };
    
    loadListingsForFacilities();
  }, [facilities, auth.token]);
  
  // Reset listings preview when facilities change
  useEffect(() => {
    if (facilities.length > 0 && previewFacilityIndex >= facilities.length) {
      setPreviewFacilityIndex(facilities.length - 1);
      setPreviewListingIndex(0);
    }
  }, [facilities, previewFacilityIndex]);
  
  // Provide values to consumers
  const contextValue = useMemo(() => ({
    // State
    listingsByFacilityId,
    previewListingIndex,
    previewFacilityIndex,
    showDelListModal,
    listToRemove,
    currentFacilityListings, // Expose current facility's listings
    isLoadingListingsForActiveFacility, // Expose loading state for current listings

    // Actions
    setPreviewListingIndex,
    setPreviewFacilityIndex,
    setShowDelListModal,
    setListToRemove,
    addListing,
    updateListingField,
    updateClinicalField,
    confirmRemoveListing,
    removeListing,
    cancelRemoveListing,
    getListingsForFacility
  }), [
    listingsByFacilityId,
    previewListingIndex,
    previewFacilityIndex,
    showDelListModal,
    listToRemove,
    currentFacilityListings,
    isLoadingListingsForActiveFacility,
    setPreviewListingIndex,
    setPreviewFacilityIndex,
    setShowDelListModal,
    setListToRemove,
    addListing,
    updateListingField,
    updateClinicalField,
    confirmRemoveListing,
    removeListing,
    cancelRemoveListing,
    getListingsForFacility
  ]);
  
  return (
    <ListingContext.Provider value={contextValue}>
      {children}
    </ListingContext.Provider>
  );
};

// Hook to access the listing context
export const useListing = () => {
  const context = useContext(ListingContext);
  if (!context) {
    throw new Error('useListing must be used within a ListingProvider');
  }
  return context;
};
